// register-user-submission.service.ts
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, switchMap, filter, first, map, catchError, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { RegisterUserForm, RegisterUserFormValues, SubmissionResult } from '../interfaces';
import { SendRegisterUserResponse } from '../interfaces/register-user.response';
import { CustomRouteResponseCompleted, isCustomRouteResponseCompleted } from '../../custom-route/interfaces/custom-route.response';
import { CustomRoute } from '../../custom-route/interfaces/custom-route.interface';
import { CUSTOM_ROUTE_MOBILE_MAP } from '../../custom-route/components/custom-route-mobile/config/constants';
import { CUSTOM_ROUTE_DESKTOP_MAP } from '../../custom-route/components/custom-route-desktop/config/constants';
import { RegisterUserService } from './register-user.service';
import { CustomRouteService } from '../../custom-route/services/custom-route.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { RouteDataService } from '../../custom-route/shared/map/services/route-data.service';
import { MatDialogService } from '../../../services/mat-dialog.service';
import { FormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { StandardDialog } from 'src/app/shared/dialogs';


@Injectable({
  providedIn: 'root'
})
export class RegisterUserSubmissionService {
  // Service injections
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);
  private readonly _sessionStorageService = inject(SessionStorageService);
  private readonly _registerUserService = inject(RegisterUserService);
  private readonly _customRouteService = inject(CustomRouteService);
  private readonly _routeDataService = inject(RouteDataService);
  private readonly _matDialogService = inject(MatDialogService);

  constructor() {
    // Extend dayjs with custom parse format plugin for date parsing
    dayjs.extend(customParseFormat);
  }

  // Methods
  /**
   * Initializes the component by resetting services and setting initial states.
   */
  initializeComponent(): void {
    // Reset services
    this._registerUserService.getDataRegisterUserServiceSignal();
    this._customRouteService.reset();
    this._routeDataService.resetRouteSegments();
    this._routeDataService.resetCurrentStepAndTrame();
    
    // Set initial states
    this._customRouteService.setIsWelcomeViewShown(true);
    this._sessionStorageService.setItem('customRouteMobileViewsState', CUSTOM_ROUTE_MOBILE_MAP.planner);
    this._sessionStorageService.setItem('customRouteDesktopViewsState', CUSTOM_ROUTE_DESKTOP_MAP.map);
    this._sessionStorageService.removeItem('registerUserForm');
  }

  /**
   * @param form 
   * @returns 
   */
  handleFormGroupSubmission(form: FormGroup<RegisterUserForm>): Observable<SubmissionResult> {
    if (form.invalid) {
      console.warn('FormGroup is invalid, aborting submission');
      return of({
        success: false,
        errorMessage: 'El formulario contiene errores. Por favor, verifica todos los campos.'
      });
    }

    const formValues = form.value as RegisterUserFormValues;
    const geolocationValue = form.get('geolocation')?.value ?? false;
    const startDateValue = form.get('startDate')?.value;
    const endDateValue = form.get('endDate')?.value;

    return this._processSubmission(formValues, geolocationValue, startDateValue, endDateValue);
  }

  /**
   * @param formValues
   * @param geolocationValue
   * @param startDateValue
   * @param endDateValue
   * @returns
   */ 
  private _processSubmission(
    formValues: RegisterUserFormValues,
    geolocationValue: boolean,
    startDateValue: Date | null,
    endDateValue: Date | null
  ): Observable<SubmissionResult> 
  {
    this._sessionStorageService.setItem<boolean>('geolocation', geolocationValue);

    const numberOfDays = this._calculateNumberOfDays(startDateValue, endDateValue);
    
    if (!numberOfDays) {
      return of({
        success: false,
        errorMessage: 'Fechas inválidas. Por favor, verifica las fechas seleccionadas.'
      });
    }

    this._registerUserService.setNumberOfDaysCustomRoute(numberOfDays);
    this._registerUserService.setRegisterUserFormData(formValues as unknown as RegisterUserForm);

    return this._registerUserService.sendRegisterUserData(formValues as unknown as RegisterUserForm).pipe(
      switchMap((registerResponse: SendRegisterUserResponse) => 
        this._handleRegisterSuccess(registerResponse)
      ),
      catchError((error) => this._handleSubmissionError(error))
    );
  }

  /**
   * @param registerResponse 
   * @returns 
   */
  private _handleRegisterSuccess(registerResponse: SendRegisterUserResponse): Observable<SubmissionResult> {
    // Save token
    this._customRouteService.setToken(registerResponse.token);
    this._sessionStorageService.setItem<string>('customRouteToken', registerResponse.token);
    this._sessionStorageService.setItem<number>('dayToShowIndex', 0);

    // Launch route planning and wait for completion
    return this._customRouteService.launchRoutePlanning(
      registerResponse.token, 
      this._customRouteService.currentOrder()
    ).pipe(
      filter(isCustomRouteResponseCompleted),
      first(),
      switchMap((completedResponse) => this._handleRouteCompletion(completedResponse)),
      catchError((error: HttpErrorResponse) => this._handleSubmissionError(error))
    );
  }

  /**
   * @param completedResponse 
   * @returns 
   */
  private _handleRouteCompletion(completedResponse: CustomRouteResponseCompleted): Observable<SubmissionResult> {
    if (completedResponse.status !== 200) {
      return of({
        success: false,
        errorMessage: 'Error en el procesamiento de la ruta. Inténtalo de nuevo.'
      });
    }

    return this._customRouteService.getCustomRouteResponse(
      this._sessionStorageService.getItem('customRouteToken'),
      this._customRouteService.currentOrder()
    ).pipe(
      map((routeResponse: CustomRouteResponseCompleted) => {
        this._registerUserService.setIsLoadingFirstCustomRoute(false);

        if (routeResponse.dailyRoutes.points.length > 0) {
          return this._handleSuccessfulRoute(routeResponse);
        } else {
          this._showNoPointsDialog();
          return { success: false, errorMessage: 'No se encontraron puntos para la ruta.' };
        }
      }),
      catchError((error: HttpErrorResponse) => this._handleSubmissionError(error))
    );
  }

  /**
   * @param routeResponse 
   * @returns 
   */
  private _handleSuccessfulRoute(routeResponse: CustomRouteResponseCompleted): SubmissionResult {
    this._customRouteService.addDailyRoute(routeResponse);
    this._sessionStorageService.setItem<CustomRoute>('customRouteData', this._customRouteService.customRouteData());
    
    const newOrder = this._customRouteService.currentOrder() + 1;
    this._customRouteService.setCurrentOrder(newOrder);
    this._sessionStorageService.setItem<number>('customRouteCurrentOrder', newOrder);

    return {
      success: true,
      shouldNavigate: true
    };
  }

  /**
   * @param error 
   * @returns 
   */
  private _handleSubmissionError(error: HttpErrorResponse): Observable<SubmissionResult> {
    this._registerUserService.setIsLoadingFirstCustomRoute(false);
    
    const errorMessage = error?.error?.error || 'No se ha podido enviar el registro. Inténtalo de nuevo más tarde.';
    this._showServerErrorDialog(errorMessage);
    this._sessionStorageService.removeAll();

    return of({
      success: false,
      errorMessage
    });
  }

  /**
   * @param startDate 
   * @param endDate 
   * @returns 
   */
  private _calculateNumberOfDays(startDate: Date | null, endDate: Date | null): number | null {
    if (!startDate || !endDate) {
      return null;
    }

    const start = dayjs(startDate, 'DD/MM/YYYY');
    const end = dayjs(endDate, 'DD/MM/YYYY');

    if (!start.isValid() || !end.isValid()) {
      return null;
    }

    return end.diff(start, 'day') + 1;
  }

  /**
   * Shows a dialog indicating that no points could be created for the route.
   */
  private _showNoPointsDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: StandardDialog,
      data: {
        title: this._translateService.instant('Parece que hay algo que no cuadra'),
        content: this._translateService.instant('Los recursos elegidos no nos permiten crear una ruta. Prueba a cambiar la selección e inténtalo de nuevo.'),
        primaryActionButtonText: this._translateService.instant('Volver a intentarlo'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    });
  }

  /**
   * 
   * @param errorMessage 
   */
  private _showServerErrorDialog(errorMessage: string): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: StandardDialog,
      data: {
        title: this._translateService.instant('La página no está disponible temporalmente'),
        content: this._translateService.instant(errorMessage),
        primaryActionButtonText: this._translateService.instant('Volver a intentarlo'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    });
  }

  /**
   * Navigates to the custom route page.
   */
  public navigateToCustomRoute(): void {
    this._router.navigate([`/custom-route`]);
  }
}